// Brand theming: turns a client's `brand` config into CSS custom properties
// (`--rf-*`) that src/App.js and src/RateCatalog.jsx read via var(...).
//
// The house client (today's ReportForge) must look exactly as it did before
// the colours were tokenised, so every distinct house colour has its own
// token and the house always gets exact hex literals, never a computed mix.

import { isHexColor } from "./wcagContrast";

// Recursively freezes an object/array and everything it holds, so nothing
// downstream can mutate a shared constant either at the top level or in one
// of its nested objects (e.g. HOUSE_BRAND.colors).
function deepFreeze(value) {
  if (value && typeof value === "object" && !Object.isFrozen(value)) {
    Object.values(value).forEach(deepFreeze);
    Object.freeze(value);
  }
  return value;
}

// The 7 base colours a client config can set.
export const HOUSE_BRAND = deepFreeze({
  displayName: "ReportForge", // exactly the current <title> in public/index.html
  logoUrl: null,
  faviconUrl: null,
  mode: "dark",
  font: "DM Sans",
  colors: {
    primary: "#6366f1",
    accent:  "#818cf8",
    sidebar: "#111827",
    surface: "#1e293b",
    text:    "#e2e8f0",
    muted:   "#64748b",
    danger:  "#f87171",
  },
});

// The 10 derived tokens' exact house values. Emitted verbatim whenever the
// merged base colours are the house's own.
export const HOUSE_DERIVED = deepFreeze({
  "bg":               "#0f172a",
  "border":           "#1e3a5f",
  "text-dim":         "#475569",
  "text-faint":       "#334155",
  "primary-soft":     "#8b5cf6",
  "text-heading":     "#f1f5f9",
  "text-heading-alt": "#f8fafc",
  "text-soft":        "#94a3b8",
  "text-softer":      "#cbd5e1",
  "danger-strong":    "#dc2626",
});

const BASE_KEYS = ["primary", "accent", "sidebar", "surface", "text", "muted", "danger"];

// Derivations for a non-house brand. `recede` is the mode's background
// direction (black in dark mode, white in light mode); `pop` is the opposite
// (towards more contrast against the background).
function derivedMixes(recede, pop) {
  return {
    // Page background: the surface pushed further into the background.
    "bg":               `color-mix(in srgb, var(--rf-surface) 65%, ${recede})`,
    // Card/input borders: surface tinted with a little of the primary.
    "border":           `color-mix(in srgb, var(--rf-primary) 20%, var(--rf-surface))`,
    // Secondary labels: muted, one step closer to the background.
    "text-dim":         `color-mix(in srgb, var(--rf-muted) 75%, ${recede})`,
    // Faintest text (taglines, counts): muted, two steps closer to the background.
    "text-faint":       `color-mix(in srgb, var(--rf-muted) 50%, ${recede})`,
    // Second stop of the brand gradient: primary blended with the accent.
    "primary-soft":     `color-mix(in srgb, var(--rf-primary) 60%, var(--rf-accent))`,
    // Headings: body text with more contrast against the background.
    "text-heading":     `color-mix(in srgb, var(--rf-text) 70%, ${pop})`,
    // Brand wordmark / modal titles: highest-contrast text.
    "text-heading-alt": `color-mix(in srgb, var(--rf-text) 50%, ${pop})`,
    // Soft body text: halfway between text and muted.
    "text-soft":        `color-mix(in srgb, var(--rf-text) 50%, var(--rf-muted))`,
    // Softer-than-text values: mostly text, a little muted.
    "text-softer":      `color-mix(in srgb, var(--rf-text) 75%, var(--rf-muted))`,
    // Solid danger fills (white text on it): danger darkened. Mode-independent
    // on purpose, since it carries white text in both modes.
    "danger-strong":    `color-mix(in srgb, var(--rf-danger) 75%, black)`,
  };
}

function mergedColors(brand) {
  const colors = { ...HOUSE_BRAND.colors };
  const given = brand?.colors || {};
  for (const k of BASE_KEYS) {
    // Only ever accept a value that is provably a hex colour. This CSS gets
    // dropped straight into a <style> block (see buildThemeCss/applyTheme),
    // so anything else — including a valid CSS colour name — is a potential
    // injection vector and falls back to the house value instead.
    if (typeof given[k] === "string" && isHexColor(given[k].trim())) colors[k] = given[k].trim();
  }
  return colors;
}

function isHouseColors(colors) {
  return BASE_KEYS.every(k => colors[k].toLowerCase() === HOUSE_BRAND.colors[k].toLowerCase());
}

// Pure: returns the :root { ... } CSS text for a brand. No DOM access, so
// this is the part covered by tools/verify-theme.mjs.
export function buildThemeCss(brand) {
  const colors = mergedColors(brand);
  const mode = brand?.mode === "light" ? "light" : "dark";
  const derived = isHouseColors(colors)
    ? HOUSE_DERIVED
    : derivedMixes(mode === "light" ? "white" : "black", mode === "light" ? "black" : "white");
  const lines = [
    ...BASE_KEYS.map(k => `  --rf-${k}: ${colors[k]};`),
    ...Object.keys(HOUSE_DERIVED).map(k => `  --rf-${k}: ${derived[k]};`),
  ];
  return `:root {\n${lines.join("\n")}\n}`;
}

// Side-effecting: applies the brand to the live document. DOM-dependent and
// not unit tested.
export function applyTheme(brand) {
  let styleEl = document.getElementById("rf-theme-vars");
  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.id = "rf-theme-vars";
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = buildThemeCss(brand);
  document.title = brand?.displayName || HOUSE_BRAND.displayName;
  if (brand?.faviconUrl) {
    const favicon = document.querySelector('link[rel="icon"]');
    if (favicon) favicon.href = brand.faviconUrl;
  }
}
